import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BrandLockup } from '@/components/BrandMark';
import { GitConnectionHelper, type GitConnectionValue } from '@/components/helpers/GitConnectionHelper';
import { TechStackHelper } from '@/components/helpers/TechStackHelper';
import { ConventionUploadHelper } from '@/components/helpers/ConventionUploadHelper';
import { hasCapability } from '@/context/ProjectContext';
import { AppShell } from '@/layouts/AppShell';
import {
  useCreateProjectMutation,
  useGetCurrentUserQuery,
  useSetProjectConfigsMutation,
  useSetProjectCredentialMutation,
  useUploadConventionMutation
} from '@/services/api';
import { PasswordInput } from '@/components/PasswordInput';
import { parseApiError } from '@/utils/apiError';
import { useToast } from '@/components/toast/ToastProvider';
import { slugify } from '@/utils/slug';
import { emptyTechStack, techStackOrNull, type TechStack } from '@/utils/techStack';
import {
  AGENT_ROLES,
  defaultReasoningMap,
  SANDBOX_MAPPING,
  defaultRetryMap,
  defaultPauseAfterMap,
  defaultApprovalSlaMap,
  DEFAULT_APPROVAL_SLA_MINUTES,
  DEFAULT_MAX_CONFLICT_RETRIES,
  MAX_CONFLICT_RETRIES_MIN,
  MAX_CONFLICT_RETRIES_MAX,
  type ReasoningEffort,
  type RetryPolicy
} from '@/utils/agentConfig';
import type { AgentRole } from '@/types/api';


const STEPS = [
  { key: 'name', label: 'Name & description', sub: 'project metadata' },
  { key: 'git', label: 'Git & credentials', sub: 'repo · token · Codex key' },
  { key: 'stack', label: 'Tech stack', sub: 'detect & declare' },
  { key: 'conventions', label: 'Conventions', sub: 'upload · optional' },
  { key: 'agents', label: 'Agent configuration', sub: '6 agents' },
  { key: 'sla', label: 'Set SLA', sub: 'approval turnaround' },
  { key: 'retry', label: 'Retry policy · review', sub: 'finalise & create' }
] as const;

type StepKey = (typeof STEPS)[number]['key'];

const FIRST_STEP = STEPS[0];

export interface DraftState {
  name: string;
  description: string;
  git: GitConnectionValue | null;
  techStack: TechStack;
  codexApiKey: string;
  reasoning: Record<AgentRole, ReasoningEffort>;
  pauseAfter: Record<AgentRole, boolean>;
  approvalSla: Record<AgentRole, number>;
  retry: Record<AgentRole, RetryPolicy>;
  maxConflictRetries: number;
  lastStep: StepKey;
}

function emptyDraft(): DraftState {
  return {
    name: '',
    description: '',
    git: null,
    techStack: emptyTechStack(),
    codexApiKey: '',
    lastStep: FIRST_STEP.key,
    reasoning: defaultReasoningMap(),
    pauseAfter: defaultPauseAfterMap(),
    approvalSla: defaultApprovalSlaMap(),
    retry: defaultRetryMap(),
    maxConflictRetries: DEFAULT_MAX_CONFLICT_RETRIES
  };
}

const DRAFT_KEY = 'wizard-draft';

export function WizardPage(): JSX.Element {
  const userQ = useGetCurrentUserQuery();

  if (userQ.isLoading) return <div className="content" style={{ paddingTop: 48 }}>Loading…</div>;
  if (userQ.data && !hasCapability(userQ.data, 'project:create')) {
    return (
      <AppShell project={null} user={userQ.data} crumbs={[{ label: 'New project' }]}>
        <div className="empty-card">
          <p className="empty-body" style={{ fontSize: 16 }}>
            You don&apos;t have permission to create projects — contact your administrator.
          </p>
        </div>
      </AppShell>
    );
  }

  return <WizardFlow userId={userQ.data?.user_id ?? 'anon'} />;
}

function WizardFlow({ userId }: { userId: string }): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const { step: stepParam } = useParams<{ step?: string }>();
  const [createProject, { isLoading: isCreating }] = useCreateProjectMutation();
  const [setConfigs] = useSetProjectConfigsMutation();
  const [setCredential] = useSetProjectCredentialMutation();
  const [uploadConvention] = useUploadConventionMutation();
  const [conventionFiles, setConventionFiles] = useState<File[]>([]);

  const storageKey = `${DRAFT_KEY}:${userId}`;
  const [draft, setDraft] = useState<DraftState>(() => loadDraft(storageKey));
  const [createError, setCreateError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false);

  const foundIndex = STEPS.findIndex((s) => s.key === stepParam);
  const stepIndex = foundIndex < 0 ? 0 : foundIndex;
  const current = STEPS[stepIndex] ?? FIRST_STEP;

  useEffect(() => {
    if (!stepParam) {
      const resumeKey = STEPS.some((s) => s.key === draft.lastStep) ? draft.lastStep : FIRST_STEP.key;
      navigate(`/projects/new/${resumeKey}`, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (!stepParam) return;
    if (submittedRef.current) return;
    saveDraft(storageKey, { ...draft, lastStep: current.key });
  }, [draft, current.key, stepParam, storageKey]);

  function goToStep(i: number): void {
    const clamped = Math.min(STEPS.length - 1, Math.max(0, i));
    navigate(`/projects/new/${(STEPS[clamped] ?? FIRST_STEP).key}`);
  }

  const stepValid = useMemo<Record<StepKey, boolean>>(
    () => ({
      name: draft.name.trim().length >= 3 && draft.name.trim().length <= 120,
      git: (draft.git?.git_url ?? '').trim() !== '' && draft.git?.protocol !== undefined,
      stack: true,
      conventions: true,
      agents: true,
      sla: AGENT_ROLES.every(
        (r) =>
          r.role === 'documenter' ||
          !draft.pauseAfter[r.role] ||
          (Number.isInteger(draft.approvalSla[r.role]) && draft.approvalSla[r.role] >= 1)
      ),
      retry: true
    }),
    [draft]
  );

  const allRequiredComplete = stepValid.name && stepValid.git;

  async function onCreate(): Promise<void> {
    if (submitting) return;
    setSubmitting(true);
    setCreateError(null);
    let created: { project_id: string; slug: string } | null = null;
    try {
      const res = await createProject({
        name: draft.name.trim(),
        description: draft.description.trim() || undefined,
        git_url: draft.git?.git_url,
        git_protocol: draft.git?.protocol,
        ...(draft.git?.git_username !== undefined ? { git_username: draft.git.git_username } : {}),
        ...(draft.git?.git_auth_mode !== undefined ? { git_auth_mode: draft.git.git_auth_mode } : {}),
        ...(draft.git?.default_branch !== undefined
          ? { default_branch: draft.git.default_branch }
          : {}),
        credential_id: draft.git?.credential_id,
        ...(draft.git?.credential_plaintext
          ? { inline_credential: { credential_type: 'git_https_token', plaintext: draft.git.credential_plaintext } }
          : {}),
        tech_stack: techStackOrNull(draft.techStack)
      }).unwrap();
      created = { project_id: res.project_id, slug: res.slug };
    } catch (err) {
      setCreateError(parseApiError(err).message);
      setSubmitting(false);
      return;
    }

    let softError: string | null = null;
    try {
      await setConfigs({
        projectId: created.project_id,
        body: {
          agent_config: Object.fromEntries(
            AGENT_ROLES.map((r) => [
              r.role,
              {
                reasoning_effort: draft.reasoning[r.role],
                ...(r.role !== 'documenter' ? { pause_after: draft.pauseAfter[r.role] } : {}),
                ...(r.role !== 'documenter' && draft.pauseAfter[r.role]
                  ? { approval_sla_minutes: draft.approvalSla[r.role] }
                  : {})
              }
            ])
          ),
          retry_policy: Object.fromEntries(
            AGENT_ROLES.map((r) => [r.role, draft.retry[r.role]])
          ),
          max_conflict_retries: draft.maxConflictRetries
        }
      }).unwrap();
      if (draft.codexApiKey.trim() !== '') {
        await setCredential({
          projectId: created.project_id,
          credential_type: 'codex_api_key',
          plaintext: draft.codexApiKey.trim()
        }).unwrap();
      }
      for (const file of conventionFiles) {
        await uploadConvention({ projectId: created.project_id, file }).unwrap();
      }
    } catch (err) {
      softError = parseApiError(err).message;
    }

    submittedRef.current = true;
    clearDraft(storageKey);
    setDraft(emptyDraft());
    setConventionFiles([]);
    if (softError) {
      toast.error(`Project created, but some settings didn't apply: ${softError}`);
    } else {
      toast.success('Project created.');
    }
    navigate(`/p/${created.slug || created.project_id}/dashboard`);
  }

  return (
    <div className="wiz">
      <div className="wiz-top">
        <BrandLockup />
        <button className="btn ghost" onClick={() => navigate('/p')}>
          ✕&nbsp;&nbsp;Exit wizard
        </button>
      </div>

      <div className="wiz-grid">
        <aside className="rail">
          <h1 className="rail-title">New project</h1>
          <p className="rail-sub mono">{STEPS.length} steps · save as draft any time</p>
          <ol className="steps">
            {STEPS.map((s, i) => (
              <li
                key={s.key}
                className={`step ${i === stepIndex ? 'active' : ''} ${i < stepIndex ? 'done' : ''}`}
                onClick={() => goToStep(i)}
              >
                <div className="step-num">
                  <span>{i + 1}</span>
                </div>
                <div>
                  <div className="step-label">{s.label}</div>
                  <div className="step-sub">{s.sub}</div>
                </div>
              </li>
            ))}
          </ol>
        </aside>

        <section className="wiz-body">
          <div className="panel">
            <div className="step-eyebrow page-eyebrow">
              Step {stepIndex + 1} of {STEPS.length}
            </div>

            {current.key === 'name' && <StepName draft={draft} setDraft={setDraft} />}
            {current.key === 'git' && <StepGit draft={draft} setDraft={setDraft} />}
            {current.key === 'stack' && <StepStack draft={draft} setDraft={setDraft} />}
            {current.key === 'conventions' && (
              <StepConventions onChange={setConventionFiles} />
            )}
            {current.key === 'agents' && <StepAgents draft={draft} setDraft={setDraft} />}
            {current.key === 'sla' && <StepSla draft={draft} setDraft={setDraft} />}
            {current.key === 'retry' && (
              <StepRetryReview
                draft={draft}
                setDraft={setDraft}
                onEditStep={(k) => goToStep(STEPS.findIndex((s) => s.key === k))}
                onCreate={() => void onCreate()}
                creating={isCreating || submitting}
                createError={createError}
                canCreate={allRequiredComplete}
              />
            )}
          </div>
        </section>
      </div>

      <nav className="wiz-nav">
        <button className="btn ghost" disabled={stepIndex === 0} onClick={() => goToStep(stepIndex - 1)}>
          ← Back
        </button>
        <span className="wiz-progress mono">
          Step {stepIndex + 1} of {STEPS.length} · {current.label}
        </span>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            className="btn ghost sm"
            data-testid="wiz-save-draft"
            onClick={() => {
              saveDraft(storageKey, { ...draft, lastStep: current.key });
              navigate('/p');
            }}
          >
            Save as draft &amp; exit
          </button>
          {stepIndex < STEPS.length - 1 && (
            <button
              className="btn primary"
              data-testid="wiz-next"
              disabled={!stepValid[current.key]}
              onClick={() => goToStep(stepIndex + 1)}
            >
              Continue →
            </button>
          )}
        </div>
      </nav>
    </div>
  );
}


function StepName({ draft, setDraft }: StepProps): JSX.Element {
  const tooShort = draft.name.trim() !== '' && draft.name.trim().length < 3;
  return (
    <>
      <h2 className="step-title">Name &amp; description</h2>
      <p className="page-sub" style={{ marginTop: 0 }}>
        Identify the project. The slug is auto-generated with a collision-resolution suffix if needed.
      </p>
      <div className="field">
        <label className="field-label" htmlFor="w-name">
          Project name · required
        </label>
        <input
          id="w-name"
          className={`input ${tooShort ? 'invalid' : ''}`}
          autoComplete="off-w-name"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
        {tooShort && <div className="field-err">Name must be at least 3 characters.</div>}
      </div>
      <div className="field">
        <label className="field-label">Slug</label>
        <div className="input-prefix-wrap">
          <span className="input-prefix mono">/p/</span>
          <input className="input mono readonly" value={slugify(draft.name) || '—'} readOnly tabIndex={-1} />
        </div>
      </div>
      <div className="field">
        <label className="field-label" htmlFor="w-desc">
          Description · optional
        </label>
        <textarea
          id="w-desc"
          className="textarea"
          maxLength={2000}
          placeholder="Up to 2000 characters"
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        />
      </div>
    </>
  );
}

function StepGit({ draft, setDraft }: StepProps): JSX.Element {
  return (
    <>
      <h2 className="step-title">Git &amp; credentials</h2>
      <p className="page-sub" style={{ marginTop: 0 }}>
        Connect the repository and test it, then add the optional Codex API key that powers this
        project&apos;s agents.
      </p>
      <GitConnectionHelper
        projectId="wizard-draft"
        initial={draft.git ?? undefined}
        onConnected={(git) => setDraft({ ...draft, git })}
      />

      <div className="field" style={{ marginTop: 20 }}>
        <label className="field-label" htmlFor="w-codex-key">
          Codex API key · optional
        </label>
        <PasswordInput
          id="w-codex-key"
          className="mono"
          toggleLabel="API key"
          placeholder="sk-…"
          autoComplete="off-w-codex-key"
          value={draft.codexApiKey}
          onChange={(codexApiKey) => setDraft({ ...draft, codexApiKey })}
        />
        <div className="field-help">
          OpenAI GPT-5.3-Codex key used to run this project&apos;s pipeline agents. Stored encrypted.
          Leave blank to run in mock mode — you can add it later in Settings.
        </div>
      </div>
    </>
  );
}

function StepStack({ draft, setDraft }: StepProps): JSX.Element {
  return (
    <>
      <h2 className="step-title">Tech stack</h2>
      <p className="page-sub" style={{ marginTop: 0 }}>
        Declare the stack from the catalogue (or leave empty for a greenfield repo — agents resolve
        versions at scaffold).
      </p>
      <TechStackHelper value={draft.techStack} onChange={(techStack) => setDraft({ ...draft, techStack })} />
    </>
  );
}

function StepConventions({ onChange }: { onChange: (files: File[]) => void }): JSX.Element {
  return (
    <>
      <h2 className="step-title">Conventions</h2>
      <p className="page-sub" style={{ marginTop: 0 }}>
        Upload lint / format / style configs so agents follow your team&apos;s conventions. Skip for defaults.
      </p>
      <ConventionUploadHelper onChange={onChange} />
    </>
  );
}

function StepAgents({ draft, setDraft }: StepProps): JSX.Element {
  return (
    <>
      <h2 className="step-title">Agent configuration</h2>
      <p className="page-sub" style={{ marginTop: 0 }}>
        Set per-agent reasoning effort. Defaults pre-populated. You can leave everything as-is.
      </p>
      {AGENT_ROLES.map((r) => (
        <div className="agent-row" key={r.role}>
          <span className="agent-mark" style={{ background: r.colorVar }}>
            {r.code}
          </span>
          <div>
            <div className="agent-name">{r.label}</div>
            <div className="agent-sub">{r.sub}</div>
          </div>
          <select
            className="select sm-sel"
            aria-label={`${r.label} reasoning effort`}
            value={draft.reasoning[r.role]}
            onChange={(e) =>
              setDraft({
                ...draft,
                reasoning: { ...draft.reasoning, [r.role]: e.target.value as ReasoningEffort }
              })
            }
          >
            <option value="low">low</option>
            <option value="medium">medium</option>
            <option value="high">high</option>
          </select>
          {r.role !== 'documenter' && (
            <label className="agent-pause" title="Pause the pipeline for human approval when this role completes successfully">
              <input
                type="checkbox"
                aria-label={`${r.label} pause for approval`}
                checked={draft.pauseAfter[r.role]}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    pauseAfter: { ...draft.pauseAfter, [r.role]: e.target.checked }
                  })
                }
              />
              <span>Pause for approval</span>
            </label>
          )}
        </div>
      ))}
    </>
  );
}

const ROLE_STAGE_ID: Record<string, string> = {
  explorer: 'explore',
  architect: 'plan',
  coder: 'code',
  reviewer: 'review',
  tester: 'test',
  documenter: 'document'
};

function StepSla({ draft, setDraft }: StepProps): JSX.Element {
  const rows = AGENT_ROLES.filter((r) => r.role !== 'documenter');
  const applyDefaultToAll = (): void => {
    const next = { ...draft.approvalSla };
    for (const r of rows) if (draft.pauseAfter[r.role]) next[r.role] = DEFAULT_APPROVAL_SLA_MINUTES;
    setDraft({ ...draft, approvalSla: next });
  };
  return (
    <>
      <h2 className="step-title">Set approval SLAs</h2>
      <p className="page-sub" style={{ marginTop: 0 }}>
        Set a target time for each paused gate. Off gates use the 30-minute default.
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
        <button type="button" className="btn sm ghost" onClick={applyDefaultToAll}>
          Apply default to all
        </button>
      </div>
      {rows.map((r) => {
        const on = draft.pauseAfter[r.role];
        return (
          <div className="agent-row" key={r.role}>
            <span className="agent-mark" style={{ background: r.colorVar }}>
              {r.code}
            </span>
            <div>
              <div className="agent-name">{r.label}</div>
              <div className="agent-sub">{ROLE_STAGE_ID[r.role]}</div>
            </div>
            <span className="agent-sub" style={{ minWidth: 72, marginLeft: 'auto', textAlign: 'right' }}>
              gate: {on ? <strong style={{ color: 'var(--amber, #b45309)' }}>on</strong> : 'off'}
            </span>
            <label
              className="sla-input-wrap"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 16 }}
            >
              <input
                type="number"
                min={1}
                step={1}
                className="input sm-sel"
                style={{ width: 80 }}
                aria-label={`${r.label} approval SLA minutes`}
                disabled={!on}
                value={on ? draft.approvalSla[r.role] : DEFAULT_APPROVAL_SLA_MINUTES}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    approvalSla: {
                      ...draft.approvalSla,
                      [r.role]: Math.max(1, Math.floor(Number(e.target.value) || 0))
                    }
                  })
                }
              />
              <span className="agent-sub">minutes</span>
            </label>
          </div>
        );
      })}
    </>
  );
}

function StepRetryReview({
  draft,
  setDraft,
  onEditStep,
  onCreate,
  creating,
  createError,
  canCreate
}: StepProps & {
  onEditStep: (k: StepKey) => void;
  onCreate: () => void;
  creating: boolean;
  createError: string | null;
  canCreate: boolean;
}): JSX.Element {
  return (
    <>
      <h2 className="step-title">Retry policy</h2>
      <p className="page-sub" style={{ marginTop: 0 }}>
        Set per-role retry attempts and timeout. Backoff strategy uses recommended defaults — leave
        everything as-is to accept them.
      </p>
      <div className="retry-head">
        <span>Role</span>
        <span>Retry attempts</span>
        <span>Timeout (s)</span>
      </div>
      {AGENT_ROLES.map((r) => (
        <div className="retry-grid" key={r.role}>
          <span className="retry-role">
            {r.label} <span className="rr-sub">{SANDBOX_MAPPING[r.role].sandbox_mode}</span>
          </span>
          <input
            className="input mono"
            type="number"
            aria-label={`${r.label} retry attempts`}
            value={draft.retry[r.role].max_attempts}
            onChange={(e) =>
              setDraft({
                ...draft,
                retry: {
                  ...draft.retry,
                  [r.role]: { ...draft.retry[r.role], max_attempts: Number(e.target.value) }
                }
              })
            }
          />
          <input
            className="input mono"
            type="number"
            aria-label={`${r.label} timeout seconds`}
            value={draft.retry[r.role].timeout_seconds}
            onChange={(e) =>
              setDraft({
                ...draft,
                retry: {
                  ...draft.retry,
                  [r.role]: { ...draft.retry[r.role], timeout_seconds: Number(e.target.value) }
                }
              })
            }
          />
        </div>
      ))}

      <div className="retry-grid" style={{ marginTop: 8 }}>
        <span className="retry-role">
          Rework loop retries <span className="rr-sub">pipeline</span>
        </span>
        <input
          className="input mono"
          type="number"
          min={MAX_CONFLICT_RETRIES_MIN}
          max={MAX_CONFLICT_RETRIES_MAX}
          aria-label="Rework loop retries"
          value={draft.maxConflictRetries}
          onChange={(e) =>
            setDraft({ ...draft, maxConflictRetries: Number(e.target.value) })
          }
        />
        <span className="rr-sub" style={{ alignSelf: 'center' }}>
          Rework attempts before the run escalates for human review (default 3)
        </span>
      </div>

      <h2 className="step-title" style={{ marginTop: 28 }}>
        Review
      </h2>
      <p className="page-sub" style={{ marginTop: 0 }}>
        Verify the project setup. Edit any section to revise.
      </p>
      <div className="review-grid">
        <ReviewCard title="1 · Name & description" onEdit={() => onEditStep('name')}>
          <ReviewRow label="Name" value={draft.name || '—'} />
        </ReviewCard>
        <ReviewCard title="2 · Git & credentials" onEdit={() => onEditStep('git')}>
          <ReviewRow
            label="Protocol"
            value={draft.git?.verified ? `${draft.git.protocol.toUpperCase()} · verified ✓` : 'not verified'}
          />
          <ReviewRow
            label="Codex API key"
            value={draft.codexApiKey.trim() !== '' ? 'set · runs live' : 'not set · mock mode'}
          />
        </ReviewCard>
        <ReviewCard title="3 · Tech stack" onEdit={() => onEditStep('stack')}>
          <ReviewRow label="Frontend" value={draft.techStack.frontend.join(' · ') || '—'} />
          <ReviewRow label="Backend" value={draft.techStack.backend.join(' · ') || '—'} />
          <ReviewRow label="Database" value={draft.techStack.database.join(' · ') || '—'} />
        </ReviewCard>
        <ReviewCard title="4 · Conventions" onEdit={() => onEditStep('conventions')}>
          <ReviewRow label="Uploaded" value="optional" />
        </ReviewCard>
        <ReviewCard title="5 · Agents · 6 of 6" full onEdit={() => onEditStep('agents')}>
          <ReviewRow
            label="Reasoning effort"
            value={AGENT_ROLES.map((r) => `${r.role}:${draft.reasoning[r.role]}`).join(' · ')}
          />
          <ReviewRow
            label="Pause for approval"
            value={AGENT_ROLES.filter((r) => r.role !== 'documenter')
              .map((r) => `${r.role}:${draft.pauseAfter[r.role] ? 'on' : 'off'}`)
              .join(' · ')}
          />
        </ReviewCard>
        <ReviewCard title="6 · Retry policy" full onEdit={() => onEditStep('retry')}>
          <ReviewRow
            label="Attempts"
            value={AGENT_ROLES.map((r) => `${r.role}:${draft.retry[r.role].max_attempts}`).join(' · ')}
          />
        </ReviewCard>
      </div>

      {createError && (
        <div className="field-err" style={{ marginTop: 16 }} role="alert" data-testid="wiz-create-error">
          {createError}
        </div>
      )}

      <div className="create-bar">
        <span className="create-hint">
          {creating
            ? 'Creating the project and applying settings — this can take a few seconds.'
            : canCreate
              ? 'All required steps complete and validated.'
              : 'Complete name + a verified Git connection first.'}
        </span>
        <button
          className="btn primary"
          data-testid="wiz-create"
          disabled={!canCreate || creating}
          onClick={onCreate}
        >
          {creating ? 'Creating…' : 'Create project ✓'}
        </button>
      </div>
    </>
  );
}

function ReviewCard({
  title,
  full,
  onEdit,
  children
}: {
  title: string;
  full?: boolean;
  onEdit: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="review-card" style={full ? { gridColumn: '1/-1' } : undefined}>
      <div className="review-head">
        <span className="review-head-title">{title}</span>
        <a className="review-head-edit" onClick={onEdit}>
          Edit
        </a>
      </div>
      {children}
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="review-row">
      <span className="review-row-label">{label}</span>
      <span className="mono" style={{ textAlign: 'right' }}>
        {value}
      </span>
    </div>
  );
}

interface StepProps {
  draft: DraftState;
  setDraft: (d: DraftState) => void;
}


function loadDraft(key: string): DraftState {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return emptyDraft();
    const parsed = JSON.parse(raw) as Partial<DraftState>;
    const lastStep = STEPS.some((s) => s.key === parsed.lastStep)
      ? (parsed.lastStep as StepKey)
      : FIRST_STEP.key;
    return withoutSecrets({
      ...emptyDraft(),
      ...parsed,
      lastStep,
      reasoning: { ...defaultReasoningMap(), ...parsed.reasoning },
      pauseAfter: { ...defaultPauseAfterMap(), ...parsed.pauseAfter },
      retry: { ...defaultRetryMap(), ...parsed.retry }
    });
  } catch {
    return emptyDraft();
  }
}

export function withoutSecrets(draft: DraftState): DraftState {
  const { git, ...rest } = draft;
  return {
    ...rest,
    codexApiKey: '',
    ...(git !== undefined && git !== null
      ? (() => {
          const { credential_plaintext: _dropped, ...gitRest } = git;
          return { git: gitRest };
        })()
      : {})
  } as DraftState;
}

function saveDraft(key: string, draft: DraftState): void {
  try {
    localStorage.setItem(key, JSON.stringify(withoutSecrets(draft)));
  } catch {
  }
}

function clearDraft(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
  }
}
